import { useEffect, useRef, useState } from 'react'
import { IErrorResponse } from '../types/ErrorResponse'
import {
  IProductInstallmentsProps,
  TProductInstallment,
} from '../types/Product'
import ProductController from '../controllers/ProductController'

export const useProductInstallments = ({
  maxInstallment,
  value,
  maxInstallmentForProducer,
  enabled = false,
}: IProductInstallmentsProps & { enabled?: boolean }) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [data, setData] = useState<TProductInstallment[]>([])
  const [error, setError] = useState<string>('')

  const abortControllerRef = useRef<AbortController | null>(null)
  const prevEnabledRef = useRef<boolean>(false)

  const execute = async () => {
    if (maxInstallment <= 0 || value <= 0) return

    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      setLoading(true)
      setError('')

      const response = await ProductController.calculeIsntallments({
        maxInstallment,
        value,
        maxInstallmentForProducer,
      })

      if (controller.signal.aborted) return

      const responseError = response as IErrorResponse
      const responseData = response as TProductInstallment[]

      if (responseError.code) {
        setError(responseError.error)
      } else {
        setData(responseData)
      }
    } catch (e) {
      if (controller.signal.aborted) return

      var message = 'Erro não identificado'
      if (typeof e === 'string') {
        message = e.toUpperCase()
      } else if (e instanceof Error) {
        const err = e as Error
        message = err.message
      }
      setError(message)
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (!enabled) {
      prevEnabledRef.current = false
      return
    }

    const justOpened = !prevEnabledRef.current
    prevEnabledRef.current = true

    const delay = justOpened ? 0 : 300

    const timer = setTimeout(() => {
      execute()
    }, delay)

    return () => clearTimeout(timer)
  }, [maxInstallment, value, maxInstallmentForProducer, enabled])

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const clear = () => {
    setError('')
  }

  return {
    loading,
    data,
    error,
    execute,
    clear,
  }
}
